/**
 * Date Utilities
 * Date parsing, formatting, and manipulation
 */

/**
 * Parse YYYY-MM-DD string as local date
 * @param {string} ymd - Date in YYYY-MM-DD format
 * @returns {Date|null}
 */
export function parseYMDToLocalDate(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const parts = ymd.split('-');
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

/**
 * Parse any date format from Algolia
 * @param {string} dateStr
 * @returns {Date|null}
 */
export function parseAnyDate(dateStr) {
  if (!dateStr) return null;

  // ISO / normal Date.parse
  const d1 = new Date(dateStr);
  if (!Number.isNaN(d1.getTime())) return d1;

  // fallback for "M/D/YYYY ..."
  const datePart = String(dateStr).split(' ')[0];
  const [m, d, y] = datePart.split('/');
  if (m && d && y) {
    const d2 = new Date(Number(y), Number(m) - 1, Number(d));
    if (!Number.isNaN(d2.getTime())) return d2;
  }
  return null;
}

/**
 * Get camp start date for sorting.
 * Uses sessionStartDays (YYYYMMDD integers) first, falls back to beginDates strings.
 * @param {Object} camp
 * @returns {Date|null}
 */
export function getCampStartDate(camp) {
  // Try sessionStartDays first (YYYYMMDD integers, e.g. 20260415)
  if (camp?.sessionStartDays?.length) {
    const minDay = Math.min(...camp.sessionStartDays);
    const y = Math.floor(minDay / 10000);
    const m = Math.floor((minDay % 10000) / 100);
    const d = minDay % 100;
    const dt = new Date(y, m - 1, d);
    if (!Number.isNaN(dt.getTime())) return dt;
  }
  // Fallback to beginDates strings
  if (
    !camp?.beginDates ||
    !Array.isArray(camp.beginDates) ||
    !camp.beginDates.length
  )
    return null;
  const dates = camp.beginDates.map(parseAnyDate).filter(Boolean);
  if (!dates.length) return null;
  dates.sort((a, b) => a - b);
  return dates[0];
}

/**
 * Convert season name to date range { beginDates, endDates } in YYYY-MM-DD format.
 * Uses the same logic as dates-selection.js generateSeasons():
 *   Winter = Dec 1 – Feb end,  Spring = Mar 1 – May 31,
 *   Summer = Jun 1 – Aug 31,   Fall   = Sep 1 – Nov 30
 *
 * Picks the nearest occurrence that hasn't fully passed yet.
 * @param {string} season - 'winter' | 'spring' | 'summer' | 'fall'
 * @returns {{ beginDates: string, endDates: string } | null}
 */
export function seasonToDateRange(season) {
  if (!season) return null;
  const key = season.toLowerCase();

  const now = new Date();
  const currentYear = now.getFullYear();
  const nextYear = currentYear + 1;
  const prevYear = currentYear - 1;

  // Helper: last day of Feb for a given year (handles leap years)
  const lastFeb = (y) => new Date(y, 2, 0).getDate();

  // Build two candidate ranges (this year's and next occurrence) so we can pick
  // the nearest one that hasn't ended yet.
  const ranges = {
    winter: [
      { start: `${prevYear}-12-01`, end: `${currentYear}-02-${String(lastFeb(currentYear)).padStart(2, '0')}` },
      { start: `${currentYear}-12-01`, end: `${nextYear}-02-${String(lastFeb(nextYear)).padStart(2, '0')}` },
    ],
    spring: [
      { start: `${currentYear}-03-01`, end: `${currentYear}-05-31` },
      { start: `${nextYear}-03-01`, end: `${nextYear}-05-31` },
    ],
    summer: [
      { start: `${currentYear}-06-01`, end: `${currentYear}-08-31` },
      { start: `${nextYear}-06-01`, end: `${nextYear}-08-31` },
    ],
    fall: [
      { start: `${currentYear}-09-01`, end: `${currentYear}-11-30` },
      { start: `${nextYear}-09-01`, end: `${nextYear}-11-30` },
    ],
  };

  const candidates = ranges[key];
  if (!candidates) return null;

  const todayTs = now.getTime();

  // Pick the first candidate whose end date is in the future (or today)
  for (const c of candidates) {
    const endDate = new Date(c.end + 'T23:59:59');
    if (endDate.getTime() >= todayTs) {
      return { beginDates: c.start, endDates: c.end };
    }
  }

  // Fallback: return the last candidate (furthest in the future)
  const last = candidates[candidates.length - 1];
  return { beginDates: last.start, endDates: last.end };
}

/**
 * Convert YYYY-MM-DD string to YYYYMMDD integer for sessionStartDays comparison
 * @param {string} ymd - Date in YYYY-MM-DD format
 * @returns {number|null} - e.g. "2026-04-15" → 20260415
 */
export function dateYmdToInt(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const int = parseInt(ymd.replace(/-/g, ''), 10);
  return Number.isFinite(int) ? int : null;
}

console.log('📦 Date utils module loaded');
